<template>
  <div ref="galleryContainer">
    <slot name="progressbar">
      <div
        v-if="galleryItems.length > 1"
        class="pointer-events-none sticky z-10 float-right hidden min-h-screen center lg:flex"
        style="top: 10%; --un-translate-y: -10%; transform: translateX(-1rem)"
      >
        <div class="relative w-1 rounded bg-grey-70" style="height: 5.5rem">
          <div
            class="absolute top-0 w-1 rounded bg-brand-4 transition-all ease-linear"
            :style="{ height: `${scrollProgress}%` }"
          />
        </div>
      </div>
    </slot>
    <vf-media-gallery
      v-if="product"
      :get-item-class="getItemClasses"
      :items="_galleryItems"
      pagination
      :product-id
      @zoom="onZoom"
    >
      <template #default="{ i }">
        <div v-if="showFavorites && i === 0" class="absolute bottom-0 right-0 z-1 <lg:mr-1">
          <product-cta-favorites
            :disabled="favorites.error"
            :is-favorite
            :pending="isFavoritePending"
            @add="addFavorite"
            @remove="removeFavorite"
          />
        </div>
        <p
          v-if="modelMeasurements && i === (galleryItems.length > 2 ? 2 : 0)"
          class="pointer-events-none absolute bottom-0 left-0 mb-2 ml-2 w-fit bg-grey-80 p-1 body-4"
        >
          {{ modelMeasurements[0] }}
        </p>
      </template>
    </vf-media-gallery>
  </div>
</template>

<script lang="ts" setup>
const favorites = useFavoritesStore()
const { $feature } = useNuxtApp()
const {
  addFavorite,
  galleryItems,
  isFavorite,
  isFavoritePending,
  modelMeasurements,
  onZoom,
  product,
  productId,
  removeFavorite,
  showFavorites
} = usePdp()
const { y: windowScrollY } = useScroll(window, {
  throttle: 16,
  behavior: 'smooth'
})

const INITIAL_PROGRESS = 10
const galleryContainer = ref<HTMLElement>()
const scrollProgress = ref(INITIAL_PROGRESS)
const { top, height } = useElementBounding(galleryContainer)

const updateScrollProgress = () => {
  if (!galleryContainer.value || height.value <= 0) {
    return scrollProgress.value = INITIAL_PROGRESS
  }
  const scrollableRange = height.value - window.innerHeight
  if (scrollableRange <= 0) return scrollProgress.value = INITIAL_PROGRESS
  const containerTop = top.value + windowScrollY.value
  const scrollPosition = Math.max(0, windowScrollY.value - containerTop)
  const rawProgress = (scrollPosition / scrollableRange) * 100
  const progress = INITIAL_PROGRESS + (rawProgress * (100 - INITIAL_PROGRESS)) / 100
  scrollProgress.value = Math.min(Math.max(progress, INITIAL_PROGRESS), 100)
}

watch(windowScrollY, updateScrollProgress)

const hasSketchfab = computed(() => !!$feature.sketchfabProductList[productId.value])

const _galleryItems = computed(() => {
  const baseItems = [...(galleryItems.value || [])]

  if (hasSketchfab.value) {
    baseItems.push({
      type: 'sketchfab',
      productId: productId.value
    })
  }

  return baseItems
})

const getItemClasses = (index: number) => {
  const count = galleryItems.value.length
  // Special cases: 1-2 images always span full width
  if (count <= 2) return 'col-span-2'
  // Define patterns for specific counts
  const patterns = {
    3: [1, 1, 2],
    4: [1, 1, 2, 2],
    5: [1, 1, 2, 1, 1],
    6: [1, 1, 2, 2, 1, 1],
    7: [1, 1, 2, 2, 1, 1, 2],
    8: [1, 1, 2, 2, 1, 1, 2, 2],
    9: [1, 1, 2, 2, 1, 1, 2, 2, 2],
    10: [1, 1, 2, 2, 1, 1, 2, 1, 1, 2],
    11: [1, 1, 2, 2, 1, 1, 2, 1, 1, 2, 2]
  }
  // Use specific pattern if available, otherwise use the base repeating pattern
  if (patterns[count]) {
    return `col-span-${patterns[count][index]}`
  }
  // For 12+ images, use the 11-image pattern and repeat
  const basePattern = patterns[11]
  const patternIndex = index % basePattern.length
  return `col-span-${basePattern[patternIndex]}`
}
</script>
